import type { ExtraColumnTarget, SheetMapping, SheetMetadataField } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import type { PureMetadata } from '../../../Model/Sample/ISampleMetaData.ts'
import type { ICoordinates } from '../../../Model/Sample/ISample.ts'
import type { CSVMappingResult } from '../../../View/Page/HomePage/Modals/CSVModalTypes.ts'
import {
  CollectionTypeConfig,
  ExtractionKitConfig,
  HabitatTypeConfig,
  LocalityTypeConfig,
  MatrixTypeConfig,
  SequencerConfig,
  SequencingChemistryConfig,
  TrapTypeConfig
} from '../../../View/Page/HomePage/Modals/MetadataHandling/ForEnums/MetadataEnumConfig.ts'
import {
  SamplingDateConfig,
  SamplingEndConfig,
  SamplingStartConfig
} from '../../../View/Page/HomePage/Modals/MetadataHandling/ForDateTime/DateTimeConfig.ts'
import { SequencingDepthConfig } from '../../../View/Page/HomePage/Modals/MetadataHandling/ForStrings/MetadataStringConfig.ts'
import { parseDateOrUndefined } from '../../../View/Page/HomePage/Modals/MetadataHandling/ForDateTime/CSVDateTimeParsing.ts'
import { readCell } from '../../../View/Page/HomePage/Modals/MetadataHandling/CSVClearSentinel.ts'

type EnumConfig = {
  enumType: Record<string, { key: string; label: string }>
  setter: (p: PureMetadata, value: never) => void
  setterCustom: (p: PureMetadata, value: string) => void
}

const ENUM_CONFIGS: Partial<Record<SheetMetadataField, EnumConfig>> = {
  matrixType: MatrixTypeConfig,
  extractionKit: ExtractionKitConfig,
  trapType: TrapTypeConfig,
  collectionType: CollectionTypeConfig,
  sequencer: SequencerConfig,
  sequencingChemistry: SequencingChemistryConfig,
  localityType: LocalityTypeConfig,
  habitatType: HabitatTypeConfig
}

const DATE_CONFIGS: Partial<Record<SheetMetadataField, (p: PureMetadata, value: string) => void>> = {
  samplingDate: SamplingDateConfig.setter,
  samplingStart: SamplingStartConfig.setter,
  samplingEnd: SamplingEndConfig.setter
}

const STRICT_DECIMAL = /^[+-]?(\d+(\.\d*)?|\.\d+)$/

/** A coordinate as written, or `undefined` for anything else: no empty part, exponent, unit or out-of-range value. */
export const parseCoordinate = (value: string | undefined, limit: 90 | 180): number | undefined => {
  const text = value?.trim() ?? ''
  if (!STRICT_DECIMAL.test(text)) {
    return undefined
  }
  const number = Number(text)
  return Math.abs(number) <= limit ? number : undefined
}

type CoordinateColumns = Pick<SheetMapping['metadata'], 'coordinatesMerged' | 'coordinatesLat' | 'coordinatesLon'>

export type CoordinateReading = { kind: 'none' } | { kind: 'point'; point: ICoordinates } | { kind: 'invalid'; text: string }

/**
 * A merged cell reads "lat,lon", as the classic import labels it; the result is GeoJSON, `[lon, lat]`.
 * Separate columns may use a decimal comma, as the classic validation allows.
 */
export const readCoordinates = (row: Record<string, string>, columns: CoordinateColumns): CoordinateReading => {
  const point = (lat: number | undefined, lon: number | undefined, text: string): CoordinateReading => {
    if (lat === undefined || lon === undefined) {
      return { kind: 'invalid', text }
    }
    /* 0,0 is a placeholder, not a sampling site */
    return lat === 0 && lon === 0 ? { kind: 'none' } : { kind: 'point', point: { type: 'Point', coordinates: [lon, lat] } }
  }
  if (columns.coordinatesMerged) {
    const cell = row[columns.coordinatesMerged]?.trim() ?? ''
    if (!cell) {
      return { kind: 'none' }
    }
    const parts = cell.split(',')
    return parts.length === 2 ? point(parseCoordinate(parts[0], 90), parseCoordinate(parts[1], 180), cell) : { kind: 'invalid', text: cell }
  }
  if (columns.coordinatesLat && columns.coordinatesLon) {
    const lat = row[columns.coordinatesLat]?.trim() ?? ''
    const lon = row[columns.coordinatesLon]?.trim() ?? ''
    if (!lat && !lon) {
      return { kind: 'none' }
    }
    return point(parseCoordinate(lat.replace(',', '.'), 90), parseCoordinate(lon.replace(',', '.'), 180), `${lat} / ${lon}`)
  }
  return { kind: 'none' }
}

/** Rows whose coordinates cannot be read; they are written without coordinates. `skipRows` get no metadata anyway. */
export const unreadableCoordinates = (
  sheet: {
    rows: Record<string, string>[]
    mapping: SheetMapping
    extraColumns: Record<string, ExtraColumnTarget>
  },
  skipRows: ReadonlySet<number> = new Set()
): { rowIndex: number; text: string }[] => {
  const columns = effectiveMetadata(sheet.mapping, sheet.extraColumns)
  return sheet.rows.flatMap((row, rowIndex) => {
    if (skipRows.has(rowIndex)) {
      return []
    }
    const reading = readCoordinates(row, columns)
    return reading.kind === 'invalid' ? [{ rowIndex, text: reading.text }] : []
  })
}

export type RowImport = {
  result: CSVMappingResult
  /** Cells the silent import could not read, as `column: value`. */
  skipped: string[]
}

/** The metadata fields a row feeds: the mapping's, plus unclaimed columns the user pointed at a field. */
export const effectiveMetadata = (mapping: SheetMapping, extraColumns: Record<string, ExtraColumnTarget>): SheetMapping['metadata'] => {
  const chosen = Object.entries(extraColumns).filter(
    (entry): entry is [string, SheetMetadataField] => entry[1] !== 'custom' && entry[1] !== 'drop'
  )
  return { ...mapping.metadata, ...Object.fromEntries(chosen.map(([column, field]) => [field, column])) }
}

/**
 * One sheet row in the classic import's row shape, keyed to an existing item by its id. An enum cell not
 * naming a member (key or label, any case) is kept as the field's custom text, and an unreadable date is
 * left out and reported, where the classic import would stop and ask.
 */
export const rowImport = (
  row: Record<string, string>,
  sheet: { mapping: SheetMapping; extraColumns: Record<string, ExtraColumnTarget> },
  itemId: string,
  primers: { forward?: string; reverse?: string },
  /** Used when the row names no sample, e.g. the name taken from the file. */
  fallbackName?: string
): RowImport => {
  const mapping = { ...sheet.mapping, metadata: effectiveMetadata(sheet.mapping, sheet.extraColumns) }
  const metadata: PureMetadata = {}
  const skipped: string[] = []
  for (const [field, column] of Object.entries(mapping.metadata) as [SheetMetadataField, string][]) {
    const cell = readCell(row, column)
    if (cell.kind !== 'value') {
      continue
    }
    const enumConfig = ENUM_CONFIGS[field]
    const dateSetter = DATE_CONFIGS[field]
    if (enumConfig) {
      const wanted = cell.value.toLowerCase()
      const member = Object.values(enumConfig.enumType).find((m) => m.key.toLowerCase() === wanted || m.label.toLowerCase() === wanted)
      if (member) {
        enumConfig.setter(metadata, member.key as never)
      } else {
        enumConfig.setterCustom(metadata, cell.value)
      }
    } else if (dateSetter) {
      const iso = parseDateOrUndefined(cell.value, { assumeLocal: true })
      if (iso) {
        dateSetter(metadata, iso)
      } else {
        skipped.push(`${column}: ${cell.value}`)
      }
    } else if (field === 'sequencingDepth') {
      SequencingDepthConfig.setter(metadata, cell.value)
    }
  }

  const custom = Object.entries(sheet.extraColumns)
    .filter(([column, target]) => target === 'custom' && row[column]?.trim())
    .map(([column]) => [column, row[column].trim()] as const)
  if (custom.length > 0) {
    metadata.customAttributes = Object.fromEntries(custom)
  }

  const coordinates = readCoordinates(row, mapping.metadata)
  if (coordinates.kind === 'point') {
    metadata.sampleCoordinates = coordinates.point
  } else if (coordinates.kind === 'invalid') {
    skipped.push(`coordinates: ${coordinates.text}`)
  }

  const name = mapping.sampleName ? row[mapping.sampleName]?.trim() : undefined
  return {
    result: {
      metadata,
      customerSampleName: name || fallbackName || undefined,
      forwardPrimer: primers.forward,
      reversePrimer: primers.reverse,
      sampleId: itemId
    },
    skipped
  }
}

const cellValue = (row: Record<string, string>, column: string | undefined) => (column ? row[column]?.trim() || undefined : undefined)

/** A tag column prefixes its primer unless the primer already starts with it: an exact primer is tag + gene primer. */
const withTag = (primer: string | undefined, tag: string | undefined) => {
  if (!primer || !tag || primer.toUpperCase().startsWith(tag.toUpperCase())) {
    return primer
  }
  return tag + primer
}

/** The exact primers a sheet row declares, `undefined` per side when it declares none. */
export const sheetPrimers = (row: Record<string, string>, mapping: SheetMapping): { forward?: string; reverse?: string } => ({
  forward: withTag(cellValue(row, mapping.forwardPrimer), cellValue(row, mapping.forwardTag)),
  reverse: withTag(cellValue(row, mapping.reversePrimer), cellValue(row, mapping.reverseTag))
})

const SHOWN_EXAMPLES = 3

export const describeUnreadableCoordinates = (
  sheet: Parameters<typeof unreadableCoordinates>[0],
  skipRows?: ReadonlySet<number>
): string | undefined => {
  const unreadable = unreadableCoordinates(sheet, skipRows)
  if (unreadable.length === 0) {
    return undefined
  }
  const examples = unreadable
    .slice(0, SHOWN_EXAMPLES)
    .map(({ rowIndex, text }) => `row ${rowIndex + 1}: "${text}"`)
    .join(', ')
  return `${unreadable.length} row(s) carry coordinates that cannot be read as latitude,longitude (${examples}${unreadable.length > SHOWN_EXAMPLES ? ', …' : ''}); these samples are written without coordinates`
}
